import React, { useState } from "react";
import { Flex, Grid, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickBadge from "@/features/pathpick/components/ui/PathPickBadge";
import PathPickInlineNotice from "@/features/pathpick/components/ui/PathPickInlineNotice";
import PathPickOrderQuantityModal from "@/features/pathpick/components/order/PathPickOrderQuantityModal";
import PathPickSizeStockModal from "@/features/pathpick/components/product/PathPickSizeStockModal";
import { START_GUIDE_CARD_VIEW } from "@/features/pathpick/features/start-guide/constants";
import PathPickStartGuideSavedProductCard from "@/features/pathpick/features/start-guide/components/PathPickStartGuideSavedProductCard";
import PathPickStartGuideCard from "@/features/pathpick/features/start-guide/components/PathPickStartGuideCard";
import PathPickStartGuideDisabledHint from "@/features/pathpick/features/start-guide/components/PathPickStartGuideDisabledHint";
import PathPickStartGuideStepPanel from "@/features/pathpick/features/start-guide/components/PathPickStartGuideStepPanel";
import {
  DEMO_KREAM_OPTIONS,
  DEMO_POIZON_OPTIONS,
  DEMO_PRODUCTS,
  DEMO_REQUIRED_ORDER_QUANTITY,
} from "@/features/pathpick/features/start-guide/utils/demoProducts";
import { StartGuideDemoState } from "@/features/pathpick/features/start-guide/hooks/useStartGuideDemoState";
import { PathPickProduct } from "@/features/pathpick/types";

interface Props {
  demo: StartGuideDemoState;
  // KREAM 전용 흐름에만 붙는 파일 생성 전제 경고 — 다음으로 넘어가는 조건은 모든 흐름이 같다.
  isKreamOnly: boolean;
  // `신청 후 이용` 탭 안에서 이 화면이 몇 번째인지 — 단계 표시와 이동 라벨이 여기서 갈린다.
  stepIndex: number;
  stepTotal: number;
  onToggleSelect: (id: number) => void;
  onCompleteOrder: (ids: number[]) => void;
  onPrev: () => void;
  onNext: () => void;
}

const ORDER_HINT_ID = "start-guide-order-hint";
const NEXT_HINT_ID = "start-guide-order-next-hint";

// 액션 열 폭 — 이 열에서 가장 넓은 요소(KREAM 경고 박스) 기준으로 잡는다.
// `auto` 로 두면 안내 문구의 max-content 가 트랙 폭을 정해 상품 카드 열을 2열 임계치 아래로 밀어낸다.
// 실측상 290px 까지는 카드 2열 정렬이 유지되므로 그 안에서 고정한다.
const ACTION_COLUMN_WIDTH = "250px";
// 주문 버튼은 열보다 좁게 둔다 — 열 폭은 문구가 덜 접히도록 필요한 값이지 버튼에 필요한 값이 아니다.
const ORDER_BUTTON_MAX_WIDTH = "220px";

/**
 * 주문 안내 — 실제 주문 모달을 튜토리얼 모드로 연다.
 *
 * `isTutorial` 이면 수량 입력까지만 실제와 같고 배송지 선택·주문 생성·완료 모달은
 * 건너뛴다. 그래서 여기서 무엇을 하든 실제 주문 내역이 만들어지지 않는다(전달서 §13).
 */
function PathPickStartGuideOrderTour({
  demo,
  isKreamOnly,
  stepIndex,
  stepTotal,
  onToggleSelect,
  onCompleteOrder,
  onPrev,
  onNext,
}: Props) {
  const { t } = useI18nHelper();
  const queryClient = useQueryClient();
  const [isOrderOpen, setIsOrderOpen] = useState(false);
  const [sizeStockItem, setSizeStockItem] = useState<PathPickProduct | null>(
    null
  );

  const items = DEMO_PRODUCTS.filter((item) =>
    demo.pickedIds.includes(item.id)
  );
  const selectedItems = items.filter((item) =>
    demo.selectedIds.includes(item.id)
  );
  // 두 상품을 모두 주문해야 체험을 통과한 것으로 본다 —
  // 모달이 지정 수량을 다 채워야 제출되므로 정상 경로에서는 두 상품이 함께 담긴다.
  const hasOrderedAll =
    items.length > 0 &&
    items.every((item) => demo.orderedIds.includes(item.id));
  // 담은 상품을 모두 선택해야 주문 버튼이 열린다 — 담은 상품 작업대와 같은 규칙.
  const hasSelectedAll =
    items.length > 0 && selectedItems.length === items.length;
  // 어느 흐름에서든 다음 화면은 주문 상세다 — 볼 주문이 없으면 넘어갈 이유가 없다.
  const canGoNext = hasOrderedAll;
  // 체험을 마치면 같은 주문 모달을 다시 열 이유가 없다 — 완료 패널·`다음` 버튼과 같은 조건으로 닫는다.
  const canOrder = hasSelectedAll && !hasOrderedAll;

  // 탭의 양 끝에서만 탭 밖으로 나간다 — 그때만 `단계` 라벨을 쓴다.
  const isFirstPage = stepIndex === 0;
  const isLastPage = stepIndex === stepTotal - 1;

  const openSizeStock = (item: PathPickProduct) => {
    const kreamOptions = DEMO_KREAM_OPTIONS[item.id];
    if (kreamOptions) {
      queryClient.setQueryData(
        ["pathpick", "kream-options", item.id],
        kreamOptions
      );
    }
    const poizonOptions = DEMO_POIZON_OPTIONS[item.id];
    if (poizonOptions) {
      queryClient.setQueryData(
        ["pathpick", "poizon-options", item.id],
        poizonOptions
      );
    }
    setSizeStockItem(item);
  };

  return (
    <>
      <PathPickStartGuideCard
        header={
          <Flex flexDirection={"column"} gap={"6px"}>
            <Flex gap={"8px"} alignItems={"center"} flexWrap={"wrap"}>
              <Text
                textStyle={"body-lg-bold"}
                fontSize={"22px"}
                color={"#1D1D1B"}
              >
                {t("path_pick_guide:order_tour.title")}
              </Text>
              {/* 판매 방식에 따라 이 탭의 화면 수가 달라진다 — 한 장뿐이면 표시하지 않는다. */}
              {stepTotal > 1 && (
                <Text
                  textStyle={"caption-bold"}
                  color={"#1272EF"}
                  role={"status"}
                  aria-label={t("path_pick_guide:nav.step_aria_label")}
                >
                  {t("path_pick_guide:nav.step_label", {
                    current: stepIndex + 1,
                    total: stepTotal,
                  })}
                </Text>
              )}
            </Flex>
            <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
              {t("path_pick_guide:order_tour.description")}
            </Text>
          </Flex>
        }
        headerProps={{
          paddingBottom: "0px",
        }}
      >
        <Flex flexDirection={"column"} gap={"16px"}>
          {/* 이동 버튼은 이 단계 패널의 우측 하단에 붙는다 — 다른 탭과 같다. */}
          <PathPickStartGuideStepPanel
            title={t("path_pick_guide:order_tour.step_title")}
            titleBadge={
              <PathPickBadge
                label={t("path_pick_guide:order_tour.status_label")}
                colorVariant={"blue"}
                size={"medium"}
              />
            }
            description={t("path_pick_guide:order_tour.step_description")}
            actions={
              <Flex
                flexDirection={"column"}
                gap={"6px"}
                alignItems={"flex-end"}
              >
                <Flex gap={"8px"}>
                  <AppButton variant={"normal"} onClick={onPrev}>
                    {t(
                      isFirstPage
                        ? "path_pick_guide:nav.prev_step"
                        : "path_pick_guide:nav.prev"
                    )}
                  </AppButton>
                  <AppButton
                    variant={"primary"}
                    isDisabled={!canGoNext}
                    onClick={onNext}
                    chakraProps={
                      canGoNext
                        ? undefined
                        : { "aria-describedby": NEXT_HINT_ID }
                    }
                  >
                    {t(
                      isLastPage
                        ? "path_pick_guide:nav.next_step"
                        : "path_pick_guide:nav.next"
                    )}
                  </AppButton>
                </Flex>
                {!canGoNext && (
                  <PathPickStartGuideDisabledHint
                    id={NEXT_HINT_ID}
                    textAlign={"right"}
                  >
                    {t("path_pick_guide:order_tour.next_disabled_hint")}
                  </PathPickStartGuideDisabledHint>
                )}
              </Flex>
            }
          />

          {/* 완료 안내는 이동 버튼 바로 아래에 둔다 — 주문을 마친 자리에서 결과를 본다. */}
          {hasOrderedAll && (
            <Flex
              role={"status"}
              flexDirection={"column"}
              gap={"4px"}
              padding={"12px 14px"}
              borderRadius={"6px"}
              border={"0.5px solid #A9E0AB"}
              backgroundColor={"#E9F7EF"}
            >
              <Text textStyle={"body-sm-bold"} color={"#247A52"}>
                {t("path_pick_guide:order_tour.completed")}
              </Text>
              <Text textStyle={"caption-medium"} color={"#247A52"}>
                {t("path_pick_guide:order_tour.completed_notice")}
              </Text>
            </Flex>
          )}

          {/* 오른쪽에는 큰 주문 버튼만 둔다 — 안내 문단을 카드로 만들지 않는다(전달서 §13).
              주문 모달을 여는 지점은 이 버튼 하나뿐이다. 카드의 주문 버튼은 자리만 지킨다.
              1fr 열이 본문 끝까지 늘어나면 버튼이 카드에서 너무 멀어지므로
              카드 2장과 버튼이 들어갈 만큼으로 행 폭을 묶는다. */}
          <Grid
            templateColumns={{
              base: "1fr",
              lg: `minmax(0, 1fr) ${ACTION_COLUMN_WIDTH}`,
            }}
            gap={"16px"}
            alignItems={"center"}
            maxWidth={{ base: "none", lg: "800px" }}
          >
            <Grid
              templateColumns={"repeat(auto-fill, minmax(200px, 240px))"}
              gap={"6px"}
            >
              {items.map((item) => (
                <PathPickStartGuideSavedProductCard
                  key={item.id}
                  item={item}
                  isSelected={demo.selectedIds.includes(item.id)}
                  onToggleSelect={() => onToggleSelect(item.id)}
                  onOpenSizeStock={() => openSizeStock(item)}
                />
              ))}
            </Grid>

            <Flex flexDirection={"column"} gap={"8px"} alignItems={"center"}>
              <AppButton
                variant={"primary"}
                isDisabled={!canOrder}
                onClick={() => setIsOrderOpen(true)}
                width={"100%"}
                height={"56px"}
                chakraProps={{
                  maxWidth: { base: "none", lg: ORDER_BUTTON_MAX_WIDTH },
                  ...(canOrder ? {} : { "aria-describedby": ORDER_HINT_ID }),
                }}
              >
                <Flex
                  gap={"6px"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  {/* 아이콘은 버튼 문구색을 상속하지 않는다 — 비활성 색을 직접 맞춘다. */}
                  <CustomIcon
                    src={"cart"}
                    width={20}
                    height={20}
                    color={canOrder ? "#FFFFFF" : "#8F8F8C"}
                  />
                  <Text textStyle={"body-md-bold"}>
                    {t("path_pick_guide:order_tour.order_cta")}
                  </Text>
                </Flex>
              </AppButton>
              {!canOrder && (
                <PathPickStartGuideDisabledHint id={ORDER_HINT_ID}>
                  {t(
                    hasOrderedAll
                      ? "path_pick_guide:order_tour.order_completed_hint"
                      : "path_pick_guide:order_tour.order_disabled_hint"
                  )}
                </PathPickStartGuideDisabledHint>
              )}
              {/* 파일 생성 요청은 다음 화면(주문 상세)에서 수량이 확정된 주문에만 열린다 —
                  주문을 누르기 전에 그 전제를 경고로 알려 준다.
                  실제 주문 상세의 같은 경고(PathPickOrderDetailKreamFile)와 형태를 맞춘다. */}
              {isKreamOnly && (
                <PathPickInlineNotice
                  tone={"yellow"}
                  leadingIcon={"warning-triangle"}
                >
                  {t("path_pick_guide:order_tour.order_notice_kream")}
                </PathPickInlineNotice>
              )}
            </Flex>
          </Grid>
        </Flex>
      </PathPickStartGuideCard>

      {isOrderOpen && selectedItems.length > 0 && (
        <PathPickOrderQuantityModal
          isOpen={true}
          onClose={() => setIsOrderOpen(false)}
          items={selectedItems}
          isTutorial={true}
          // 두 상품을 지정 수량만큼 채워야 주문 요청이 열린다 — 가이드에서만 거는 조건이다.
          requiredQuantityByProductId={DEMO_REQUIRED_ORDER_QUANTITY}
          // 가이드 데모는 숫자 id로만 진행 상태를 관리한다(데모 상품끼리는 id가 겹치지 않는다).
          onOrderCompleted={(products) =>
            onCompleteOrder(products.map(({ id }) => id))
          }
        />
      )}
      {sizeStockItem && (
        <PathPickSizeStockModal
          isOpen={true}
          onClose={() => setSizeStockItem(null)}
          item={sizeStockItem}
          viewOverride={START_GUIDE_CARD_VIEW}
        />
      )}
    </>
  );
}

export default PathPickStartGuideOrderTour;
